import { createDeck, shuffle } from './cards.js';
import { evaluateHand, winners } from './evaluator.js';
import { PERSONALITIES, chooseDiscards } from './ai.js';
import { TABLE_MODES } from './currency.js';

export const PHASE = {
  IDLE: 'IDLE', BET_PRE: 'BET_PRE', DRAW: 'DRAW', BET_POST: 'BET_POST', SHOWDOWN: 'SHOWDOWN', GAME_OVER: 'GAME_OVER'
};

export const BUY_IN = TABLE_MODES.frontier.buyIn;
export const ANTE = TABLE_MODES.frontier.ante;
export const PRE_DRAW_BET = TABLE_MODES.frontier.preDrawBet;
export const POST_DRAW_BET = TABLE_MODES.frontier.postDrawBet;
export const HARD_TIMES_BALANCE = TABLE_MODES.frontier.hardTimesBalance;
export const ROBBERY_THRESHOLD = 500000;
export const ROBBERY_TAKE = 100000;

export function formatMoney(cents) {
  return `$${(Math.max(0, cents) / 100).toFixed(2)}`;
}

function createSeat(id, name, aiIndex = null, buyIn = BUY_IN) {
  return { id, name, aiIndex, stack: buyIn, cards: [], folded: false, sittingOut: false, allIn: false, roundBet: 0, handBet: 0, discardCount: null, lastAction: '' };
}

export function chooseStartingPatrons(random = Math.random) {
  const order = PERSONALITIES.map((_, index) => index);
  for (let index = order.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(random() * (index + 1));
    [order[index], order[swap]] = [order[swap], order[index]];
  }
  return order.slice(0, 3);
}

export function createGame(indexer, patronRandom = Math.random, tableMode = 'frontier') {
  const economy = TABLE_MODES[tableMode] || TABLE_MODES.frontier;
  const startingPatrons = chooseStartingPatrons(patronRandom);
  return {
    version: 1,
    handNumber: 0,
    dealer: 3,
    phase: PHASE.IDLE,
    seats: [createSeat(0, 'You', null, economy.buyIn), ...startingPatrons.map((aiIndex, index) => createSeat(index + 1, PERSONALITIES[aiIndex].name, aiIndex, economy.buyIn))],
    deck: [],
    deckIndex: 0,
    currentBet: 0,
    betUnit: economy.preDrawBet,
    raises: 0,
    pending: [],
    actingSeat: null,
    selectedDiscards: [],
    pot: 0,
    message: 'The table is waiting.',
    result: null,
    tableMode: economy.id,
    buyIn: economy.buyIn,
    ante: economy.ante,
    preDrawBet: economy.preDrawBet,
    postDrawBet: economy.postDrawBet,
    hardTimesBalance: economy.hardTimesBalance,
    extremePot: economy.extremePot,
    hardTimes: false,
    robberyOccurred: false,
    robbery: null,
    tableChips: economy.buyIn * 4,
    nextPatron: (startingPatrons.at(-1) + 1) % PERSONALITIES.length,
    turnover: null,
    indexer,
    revision: 0
  };
}

const active = state => state.seats.filter(seat => !seat.folded && !seat.allIn && seat.stack > 0);
const contenders = state => state.seats.filter(seat => !seat.folded && seat.cards.length === 5);

function nextFrom(state, from, predicate) {
  for (let offset = 1; offset <= state.seats.length; offset += 1) {
    const index = (from + offset) % state.seats.length;
    if (predicate(state.seats[index])) return index;
  }
  return null;
}

function pay(state, seat, amount) {
  const paid = Math.min(seat.stack, Math.max(0, amount));
  seat.stack -= paid;
  seat.roundBet += paid;
  seat.handBet += paid;
  state.pot += paid;
  if (seat.stack === 0) seat.allIn = true;
  return paid;
}

function dealCard(state) {
  const card = state.deck[state.deckIndex];
  if (!card) throw new Error('Deck exhausted');
  state.deckIndex += 1;
  return card;
}

function resetBetting(state, phase) {
  state.phase = phase;
  state.currentBet = 0;
  state.raises = 0;
  state.betUnit = phase === PHASE.BET_POST ? state.postDrawBet : state.preDrawBet;
  state.seats.forEach(seat => { seat.roundBet = 0; seat.lastAction = ''; });
  state.pending = active(state).map(seat => seat.id);
  state.actingSeat = nextFrom(state, state.dealer, seat => state.pending.includes(seat.id));
}

export function startHand(state) {
  if (state.seats[0].stack < state.ante) return { ...state, phase: PHASE.GAME_OVER, message: 'Your purse cannot cover the ante. Start a new table to play again.' };
  const next = structuredClone({ ...state, indexer: undefined });
  next.indexer = state.indexer;
  next.turnover = null;
  next.robbery = null;
  if (state.phase === PHASE.SHOWDOWN && state.result) {
    const occupied = new Set(next.seats.slice(1).map(seat => seat.aiIndex));
    const leaving = [];
    next.seats.slice(1).forEach(seat => {
      if (seat.sittingOut) return;
      const won = state.result.winners.includes(seat.id);
      const chance = seat.stack < next.ante ? 1 : won ? 0.07 : seat.stack < next.buyIn * 0.44 ? 0.48 : 0.16;
      if (Math.random() >= chance) return;
      let candidate = next.nextPatron;
      for (let tries = 0; tries < PERSONALITIES.length; tries += 1) {
        candidate %= PERSONALITIES.length;
        if (!occupied.has(candidate)) break;
        candidate += 1;
      }
      occupied.delete(seat.aiIndex);
      occupied.add(candidate);
      leaving.push({ from: seat.name, to: PERSONALITIES[candidate].name, angry: !won });
      next.tableChips += next.buyIn - seat.stack;
      seat.aiIndex = candidate;
      seat.name = PERSONALITIES[candidate].name;
      seat.stack = next.buyIn;
      seat.lastAction = 'TAKES A SEAT';
      next.nextPatron = (candidate + 1) % PERSONALITIES.length;
    });
    if (leaving.length) next.turnover = leaving;
  }
  next.hardTimes = next.seats[0].stack < next.hardTimesBalance;
  const availableOpponents = next.seats.slice(1).filter(seat => seat.stack >= next.ante).sort((a, b) => b.stack - a.stack || a.id - b.id);
  const hardTimesOpponent = availableOpponents[0]?.id ?? null;
  next.seats.slice(1).forEach(seat => { seat.sittingOut = next.hardTimes && seat.id !== hardTimesOpponent; });
  next.handNumber += 1;
  next.dealer = nextFrom(next, next.dealer, seat => seat.stack > 0 && !seat.sittingOut);
  next.deck = shuffle(createDeck(), state.indexer);
  next.deckIndex = 0;
  next.pot = 0;
  next.result = null;
  next.selectedDiscards = [];
  next.seats.forEach(seat => {
    seat.cards = [];
    seat.folded = seat.stack < next.ante || seat.sittingOut;
    seat.allIn = false;
    seat.roundBet = 0;
    seat.handBet = 0;
    seat.discardCount = null;
    seat.lastAction = seat.folded ? 'OUT' : '';
    if (!seat.folded) pay(next, seat, next.ante);
  });
  for (let card = 0; card < 5; card += 1) {
    for (let offset = 1; offset <= next.seats.length; offset += 1) {
      const index = (next.dealer + offset) % next.seats.length;
      if (!next.seats[index].folded) next.seats[index].cards.push(dealCard(next));
    }
  }
  resetBetting(next, PHASE.BET_PRE);
  const opener = next.seats[next.actingSeat];
  next.message = next.hardTimes
    ? `Hard times. The room clears for heads-up play. ${formatMoney(next.ante)} ante in.`
    : `Hand ${next.handNumber}. ${formatMoney(next.ante)} ante in. ${opener.id === 0 ? 'You act' : `${opener.name} acts`} first.`;
  next.revision += 1;
  return next;
}

export function getLegalActions(state, seatId = state.actingSeat) {
  if (![PHASE.BET_PRE, PHASE.BET_POST].includes(state.phase) || seatId !== state.actingSeat) return [];
  const seat = state.seats[seatId];
  if (!seat || seat.folded || seat.allIn) return [];
  const toCall = Math.max(0, state.currentBet - seat.roundBet);
  const actions = [{ type: 'FOLD', label: 'Fold' }];
  if (toCall === 0) actions.push({ type: 'CHECK', label: 'Check', amount: 0 });
  else actions.push({ type: 'CALL', label: `Call ${formatMoney(Math.min(toCall, seat.stack))}`, amount: Math.min(toCall, seat.stack) });
  if (state.raises < 3 && seat.stack > toCall) {
    const amount = Math.min(toCall + state.betUnit, seat.stack);
    actions.push({ type: state.currentBet === 0 ? 'BET' : 'RAISE', label: state.currentBet === 0 ? `Bet ${formatMoney(amount)}` : `Raise ${formatMoney(amount)}`, amount });
  }
  return actions;
}

function advanceActor(state, after) {
  if (contenders(state).length === 1) return settleUncontested(state);
  if (state.pending.length === 0) return advancePhase(state);
  state.actingSeat = nextFrom(state, after, seat => state.pending.includes(seat.id) && !seat.folded && !seat.allIn);
  if (state.actingSeat === null) return advancePhase(state);
  const actor = state.seats[state.actingSeat];
  state.message = actor.id === 0 ? 'You are considering the wager.' : `${actor.name} is considering the wager.`;
  return state;
}

export function act(state, seatId, action) {
  const legal = getLegalActions(state, seatId);
  const legalAction = legal.find(item => item.type === action.type);
  if (!legalAction) throw new Error(`Illegal action ${action.type}`);
  const next = structuredClone({ ...state, indexer: undefined });
  next.indexer = state.indexer;
  const seat = next.seats[seatId];
  if (action.type === 'FOLD') {
    seat.folded = true;
    seat.lastAction = 'FOLDS';
  } else if (action.type === 'CHECK') {
    seat.lastAction = 'CHECKS';
  } else if (action.type === 'CALL') {
    const due = next.currentBet - seat.roundBet;
    const paid = pay(next, seat, due);
    seat.lastAction = paid < due ? `ALL-IN ${formatMoney(paid)}` : `CALLS ${formatMoney(paid)}`;
  } else {
    const paid = pay(next, seat, (next.currentBet - seat.roundBet) + next.betUnit);
    next.currentBet = Math.max(next.currentBet, seat.roundBet);
    next.raises += 1;
    seat.lastAction = `${action.type === 'BET' ? 'BETS' : 'RAISES'} ${formatMoney(paid)}`;
    next.pending = active(next).filter(other => other.id !== seatId).map(other => other.id);
  }
  next.pending = next.pending.filter(id => id !== seatId && !next.seats[id].folded && !next.seats[id].allIn);
  next.revision += 1;
  return advanceActor(next, seatId);
}

function prepareDraw(state) {
  state.phase = PHASE.DRAW;
  state.actingSeat = 0;
  state.selectedDiscards = [];
  state.seats.filter(seat => seat.aiIndex !== null && !seat.folded).forEach(seat => {
    const discarded = chooseDiscards(seat.cards).slice(0, 3).sort((a, b) => b - a);
    discarded.forEach(index => seat.cards.splice(index, 1));
    seat.discardCount = discarded.length;
    while (seat.cards.length < 5) seat.cards.push(dealCard(state));
    seat.lastAction = discarded.length ? `DRAWS ${discarded.length}` : 'STANDS PAT';
  });
  state.message = 'Choose up to three cards to discard, then draw.';
  if (state.seats[0].folded) {
    resetBetting(state, PHASE.BET_POST);
    const opener = state.seats[state.actingSeat];
    state.message = opener.id === 0 ? 'You open the final betting.' : `${opener.name} opens the final betting.`;
  }
  return state;
}

function advancePhase(state) {
  if (state.phase === PHASE.BET_PRE) return prepareDraw(state);
  if (state.phase === PHASE.BET_POST) return settleShowdown(state);
  return state;
}

export function toggleDiscard(state, index) {
  if (state.phase !== PHASE.DRAW || state.seats[0].folded) return state;
  const next = structuredClone({ ...state, indexer: undefined });
  next.indexer = state.indexer;
  const selected = new Set(next.selectedDiscards);
  if (selected.has(index)) selected.delete(index);
  else if (selected.size < 3) selected.add(index);
  next.selectedDiscards = [...selected].sort();
  next.revision += 1;
  return next;
}

export function confirmDraw(state) {
  if (state.phase !== PHASE.DRAW || state.seats[0].folded) throw new Error('Cannot draw now');
  const next = structuredClone({ ...state, indexer: undefined });
  next.indexer = state.indexer;
  const discard = [...next.selectedDiscards].sort((a, b) => b - a).slice(0, 3);
  discard.forEach(index => next.seats[0].cards.splice(index, 1));
  next.seats[0].discardCount = discard.length;
  while (next.seats[0].cards.length < 5) next.seats[0].cards.push(dealCard(next));
  next.seats[0].lastAction = discard.length ? `DRAWS ${discard.length}` : 'STANDS PAT';
  next.selectedDiscards = [];
  resetBetting(next, PHASE.BET_POST);
  const opener = next.seats[next.actingSeat];
  next.message = `Final betting. ${opener.id === 0 ? 'You act' : `${opener.name} acts`} first.`;
  next.revision += 1;
  return next;
}

function settleUncontested(state) {
  const winner = contenders(state)[0];
  const award = state.pot;
  winner.stack += state.pot;
  state.result = { winners: [winner.id], title: winner.id === 0 ? 'You take the pot' : `${winner.name} takes the pot`, detail: `${formatMoney(award)} — everyone else folded.` };
  state.pot = 0;
  state.phase = PHASE.SHOWDOWN;
  state.actingSeat = null;
  state.message = state.result.title;
  return triggerRobberyIfNeeded(state);
}

function settleShowdown(state) {
  const award = state.pot;
  const eligibleSeats = contenders(state);
  const contributionLevels = [...new Set(state.seats.map(seat => seat.handBet).filter(Boolean))].sort((a, b) => a - b);
  const awarded = new Set();
  let previous = 0;

  contributionLevels.forEach(level => {
    const funders = state.seats.filter(seat => seat.handBet >= level);
    const layer = (level - previous) * funders.length;
    const eligible = eligibleSeats.filter(seat => seat.handBet >= level);
    if (!eligible.length || layer <= 0) return;
    const best = winners(eligible.map(seat => ({ id: seat.id, cards: seat.cards })));
    const ordered = [...best].sort((a, b) => ((a.id - state.dealer + state.seats.length) % state.seats.length) - ((b.id - state.dealer + state.seats.length) % state.seats.length));
    const share = Math.floor(layer / ordered.length);
    let remainder = layer - share * ordered.length;
    ordered.forEach(hand => {
      state.seats[hand.id].stack += share + (remainder > 0 ? 1 : 0);
      remainder = Math.max(0, remainder - 1);
      awarded.add(hand.id);
    });
    previous = level;
  });

  const winningSeats = [...awarded].map(id => state.seats[id]);
  const lead = winningSeats[0];
  const evaluation = evaluateHand(lead.cards);
  const winnerNames = winningSeats.map(seat => seat.name).join(' & ');
  const title = winningSeats.length === 1 && winningSeats[0].id === 0 ? 'You win the pot' : `${winnerNames} ${winningSeats.length > 1 ? 'share' : 'wins'} the pot`;
  state.result = { winners: winningSeats.map(seat => seat.id), title, detail: `${evaluation.name} — ${formatMoney(award)}.` };
  state.pot = 0;
  state.phase = PHASE.SHOWDOWN;
  state.actingSeat = null;
  state.message = state.result.title;
  return triggerRobberyIfNeeded(state);
}

export function triggerRobberyIfNeeded(state) {
  const player = state.seats[0];
  if (state.robberyOccurred || player.stack < ROBBERY_THRESHOLD) return state;
  const taken = Math.min(ROBBERY_TAKE, player.stack);
  player.stack -= taken;
  state.tableChips -= taken;
  state.robberyOccurred = true;
  state.robbery = { amount: taken, threshold: ROBBERY_THRESHOLD, remaining: player.stack };
  state.message = `The saloon is robbed! Masked outlaws take ${formatMoney(taken)} from your purse.`;
  return state;
}

export function assertIntegrity(state) {
  const cards = state.seats.flatMap(seat => seat.cards);
  if (new Set(cards).size !== cards.length) throw new Error('Duplicate card in play');
  if (state.seats.some(seat => seat.stack < 0)) throw new Error('Negative stack');
  const total = state.seats.reduce((sum, seat) => sum + seat.stack, 0) + state.pot;
  if (state.phase !== PHASE.IDLE && total !== state.tableChips) throw new Error(`Chip conservation failed: ${total}/${state.tableChips}`);
  return true;
}
